import React, { useEffect, useMemo, useState } from 'react';
import { examApi } from '../services/api';
import { Badge, Button, Card, EmptyState, ErrorState, LoadingState, PageHeader } from '../components/ui';
import { FiBookOpen, FiClock, FiLock } from 'react-icons/fi';
import { useUpgrade } from '../hooks/useUpgrade';
import AdBanner from '../components/AdBanner';
import { useAuth } from '../hooks/useAuth';
import './ExamSelectionPage.css';
import { readExamMode, saveExamMode, type ExamMode } from '../utils/examPrefs';
import ExamBadge from '../components/ExamBadge';
import { examCodeOf } from '../utils/examCatalog';
import { getApiErrorCode, getApiErrorMessage, getShortExamInfo, shortExamMessage, type ShortExamInfo } from '../utils/examStart';

interface IncorrectReviewSummary {
  examType: string;
  required: number;
  incorrectTotal: number;
  incorrectCount: number;
  fillCount: number;
  total: number;
}

interface ExamSelectionPageProps {
  onStartExam?: (sessionId: string) => void;
  onExit?: () => void;
}

interface ExamType {
  exam_type_id: string;
  name: string;
  description: string;
  icon?: string;
  exam_code?: string;
  inferred_exam_code?: string;
  total_questions: number;
  exam_duration_minutes: number;
  passing_score: number;
  exam_format?: string;
  is_active: boolean;
  /** 샘플(누구나) / 본 시험(구독자·쿠폰) / 쿠폰 전용 */
  tier?: 'sample' | 'full' | 'private';
  /** 이 사용자의 접근: locked = 목록엔 보이지만 구독이 필요 */
  access?: 'open' | 'locked';
  access_via?: 'admin' | 'free' | 'coupon' | 'subscription' | null;
}

type ExamTab = 'sample' | 'full' | 'review';
/** 목록에 없는 시험 ID에서도 코드 추출: aws-saa-c03-sample → SAA-C03 */
const codeFromId = (id: string) => {
  const match = id.match(/([a-z]{3})-?c(\d{2})/i);
  return match ? `${match[1].toUpperCase()}-C${match[2]}` : '';
};
const tabOf = (type: ExamType): ExamTab => (type.tier === 'sample' ? 'sample' : 'full');
const ACCESS_BADGE: Record<string, { label: string; tone: 'success' | 'info' | 'neutral' | 'warning' }> = {
  free: { label: '무료', tone: 'success' },
  coupon: { label: '쿠폰 등록', tone: 'info' },
  subscription: { label: '구독 포함', tone: 'info' },
  admin: { label: '관리자', tone: 'neutral' },
};

interface StartExamResponse {
  sessionId: string;
}

type MetaIconType = 'questions' | 'duration' | 'passing' | 'format' | 'defaultExam';

const MetaIcon: React.FC<{ type: MetaIconType; className?: string }> = ({ type, className }) => {
  if (type === 'questions') {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M7 6.5H17M7 12H17M7 17.5H13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <rect x="4" y="3.5" width="16" height="17" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
      </svg>
    );
  }
  if (type === 'duration') {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="13" r="7.5" stroke="currentColor" strokeWidth="1.8" />
        <path d="M12 13V9.5M12 13L14.5 15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M9 3.5H15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (type === 'passing') {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 21L14.9 15.6L20.9 14.8L16.6 10.8L17.7 4.9L12 7.8L6.3 4.9L7.4 10.8L3.1 14.8L9.1 15.6L12 21Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      </svg>
    );
  }
  if (type === 'format') {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" strokeWidth="1.8" />
        <path d="M8 9H16M8 12.5H13M8 16H12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4" y="3.5" width="16" height="17" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8 8.5H16M8 12H16M8 15.5H12.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
};

const ExamSelectionPage: React.FC<ExamSelectionPageProps> = ({ onStartExam, onExit }) => {
  const { user } = useAuth();
  const { promptUpgrade, requireAiPower } = useUpgrade();
  const aiPower = Boolean(user?.is_admin || user?.ai_power?.aiPower);
  // 체험 시작·구독으로 권한이 바뀌면 목록(잠김 여부)을 다시 받습니다
  const powerKey = user?.ai_power?.aiPower ? 1 : 0;
  const [tab, setTab] = useState<ExamTab | null>(null);
  const [examTypes, setExamTypes] = useState<ExamType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startingExam, setStartingExam] = useState<string | null>(null);
  const [confirmExamTypeId, setConfirmExamTypeId] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [shortExam, setShortExam] = useState<(ShortExamInfo & { examTypeId: string; kind?: 'incorrect-review' }) | null>(null);
  const [reviews, setReviews] = useState<IncorrectReviewSummary[]>([]);
  const [confirmReviewExamTypeId, setConfirmReviewExamTypeId] = useState<string | null>(null);
  const [selectedMode, setSelectedModeState] = useState<ExamMode>(readExamMode);
  const setSelectedMode = (mode: ExamMode) => {
    setSelectedModeState(mode);
    saveExamMode(mode);
  };

  const showAds = !user?.paid_plan || user.paid_plan === 0;

  useEffect(() => {
    const loadExamTypes = async () => {
      try {
        const response = await examApi.getTypes();
        if (response.success && response.data) {
          const list = response.data as ExamType[];
          setExamTypes(list);
          // 처음 들어오면: 열린 본 시험이 있으면 본 시험 탭, 없으면 샘플 탭
          setTab((current) => current ?? (list.some((type) => tabOf(type) === 'full' && type.access !== 'locked') ? 'full' : 'sample'));
          return;
        }
        setError('시험 유형을 불러올 수 없습니다.');
      } catch {
        setError('시험 유형을 불러오는 중 오류가 발생했습니다.');
      } finally {
        setIsLoading(false);
      }
    };

    loadExamTypes();
    // 오답 모의고사 구성은 부가 정보라 실패해도 화면은 그대로 둡니다.
    examApi.incorrectReviewSummary()
      .then((response) => setReviews(((response.data as { reviews?: IncorrectReviewSummary[] })?.reviews) || []))
      .catch(() => setReviews([]));
  }, [powerKey]);

  const examName = (examTypeId: string) => {
    // 팝업 제목이 길어지지 않게 시험 코드(SAA-C03 등)만, 없으면 이름
    return codeFromId(examTypeId) || examTypes.find((item) => item.exam_type_id === examTypeId)?.name || examTypeId;
  };

  const handleStartIncorrectReview = async (examTypeId: string, allowShort = false) => {
    setStartingExam(`review:${examTypeId}`);
    setStartError(null);
    try {
      const response = await examApi.startIncorrectReview(examTypeId, { allowShort }) as { success: boolean; data?: StartExamResponse };
      if (response.success && response.data) {
        onStartExam?.(response.data.sessionId);
      } else {
        setStartError('오답 모의고사를 시작할 수 없습니다.');
      }
    } catch (error) {
      const shortInfo = getShortExamInfo(error);
      if (shortInfo) {
        setShortExam({ ...shortInfo, examTypeId, kind: 'incorrect-review' });
        return;
      }
      if (getApiErrorCode(error) === 'AI_POWER_REQUIRED') {
        promptUpgrade('오답 모의고사', 'feature');
        return;
      }
      if (getApiErrorCode(error) === 'SUBSCRIPTION_REQUIRED') {
        promptUpgrade(examName(examTypeId), 'exam');
        return;
      }
      setStartError(
        getApiErrorCode(error) === 'ACTIVE_SESSION_EXISTS'
          ? '진행 중인 시험이 있습니다. 대시보드에서 이어서 풀거나 포기한 뒤 새로 시작하세요.'
          : getApiErrorMessage(error) || '오답 모의고사를 시작하는 중 오류가 발생했습니다.',
      );
    } finally {
      setStartingExam(null);
    }
  };

  const handleStartRetry = async (examTypeId: string) => {
    setStartingExam(`retry:${examTypeId}`);
    setStartError(null);
    try {
      const response = await examApi.startIncorrectRetry(examTypeId) as { success: boolean; data?: StartExamResponse };
      if (response.success && response.data) onStartExam?.(response.data.sessionId);
      else setStartError('틀린 문제 다시 풀기를 시작할 수 없습니다.');
    } catch (error) {
      if (getApiErrorCode(error) === 'SUBSCRIPTION_REQUIRED') {
        promptUpgrade(examName(examTypeId), 'exam');
        return;
      }
      setStartError(
        getApiErrorCode(error) === 'ACTIVE_SESSION_EXISTS'
          ? '진행 중인 시험이 있습니다. 대시보드에서 이어서 풀거나 포기한 뒤 새로 시작하세요.'
          : getApiErrorMessage(error) || '틀린 문제 다시 풀기를 시작하는 중 오류가 발생했습니다.',
      );
    } finally {
      setStartingExam(null);
    }
  };

  const modeSummary = useMemo(() => {
    return selectedMode === 'exam'
      ? '실전 모드: 시간 제한과 실제 시험 흐름을 그대로 따릅니다.'
      : '연습 모드: 문항 풀이 후 학습 중심으로 빠르게 복습합니다.';
  }, [selectedMode]);

  const getExamFormatLabel = (format?: string): '객관식' | '주관식' => {
    const normalized = (format || '').toLowerCase();
    if (normalized.includes('subjective') || normalized.includes('short') || normalized.includes('essay') || normalized.includes('주관식')) {
      return '주관식';
    }
    return '객관식';
  };

  const handleStartExam = async (examTypeId: string, allowShort = false) => {
    setStartingExam(examTypeId);
    setStartError(null);
    try {
      const response = await examApi.start(examTypeId, selectedMode, { allowShort }) as { success: boolean; data?: StartExamResponse };
      if (response.success && response.data) {
        const sessionId = response.data.sessionId;
        onStartExam?.(sessionId);
      } else {
        setStartError('시험을 시작할 수 없습니다.');
      }
    } catch (error) {
      const shortInfo = getShortExamInfo(error);
      if (shortInfo) {
        // 실전 모드인데 승인된 문항이 모자람 → 짧게 응시할지 물어봅니다.
        setShortExam({ ...shortInfo, examTypeId });
        return;
      }
      if (getApiErrorCode(error) === 'SUBSCRIPTION_REQUIRED') {
        // 잠긴 본 시험 (목록이 오래됐을 때 등): 구독 안내
        promptUpgrade(examName(examTypeId), 'exam');
        return;
      }
      setStartError(
        getApiErrorCode(error) === 'ACTIVE_SESSION_EXISTS'
          ? '진행 중인 시험이 있습니다. 대시보드에서 이어서 풀거나 포기한 뒤 새로 시작하세요.'
          : getApiErrorMessage(error) || '시험을 시작하는 중 오류가 발생했습니다.',
      );
    } finally {
      setStartingExam(null);
    }
  };

  const handleOpenStartConfirm = (examTypeId: string) => {
    if (startingExam !== null) return;
    if (examTypes.find((type) => type.exam_type_id === examTypeId)?.access === 'locked') {
      promptUpgrade(examName(examTypeId), 'exam');
      return;
    }
    setConfirmExamTypeId(examTypeId);
  };

  const handleConfirmStartExam = async () => {
    if (!confirmExamTypeId) return;
    const examTypeId = confirmExamTypeId;
    setConfirmExamTypeId(null);
    await handleStartExam(examTypeId);
  };

  if (isLoading) {
    return <LoadingState label="시험 목록을 불러오는 중..." />;
  }

  if (error) {
    return (
      <ErrorState
        title="시험 목록을 불러오지 못했습니다"
        description={error}
        action={onExit && <Button variant="secondary" onClick={onExit}>대시보드로 돌아가기</Button>}
      />
    );
  }

  return (
    <div className="exam-select-v2">
      <PageHeader
        title="시험 선택"
        description="응시 모드를 고른 뒤 시험 유형을 선택하세요."
        actions={onExit && (
          <Button variant="secondary" onClick={onExit}>
            대시보드로 돌아가기
          </Button>
        )}
      />

      <section className="exam-select-v2__mode" aria-label="응시 모드">
        <div className="exam-select-v2__segmented" role="radiogroup">
          <button
            type="button"
            role="radio"
            aria-checked={selectedMode === 'practice'}
            className={`exam-select-v2__segment ${selectedMode === 'practice' ? 'is-active' : ''}`}
            onClick={() => setSelectedMode('practice')}
          >
            <FiBookOpen aria-hidden="true" />
            연습 모드
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={selectedMode === 'exam'}
            className={`exam-select-v2__segment ${selectedMode === 'exam' ? 'is-active' : ''}`}
            onClick={() => setSelectedMode('exam')}
          >
            <FiClock aria-hidden="true" />
            실전 모드
          </button>
        </div>
        <p className="exam-select-v2__mode-desc">{modeSummary}</p>
      </section>

      {startError && (
        <p className="exam-select-v2__start-error" role="alert">{startError}</p>
      )}

      <div className="exam-select-v2__tabs" role="tablist" aria-label="시험 구분">
        {(['sample', 'full', 'review'] as ExamTab[]).filter((key) => key !== 'review' || reviews.length > 0).map((key) => {
          const count = key === 'review' ? reviews.length : examTypes.filter((type) => tabOf(type) === key).length;
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={(tab ?? 'sample') === key}
              className={`exam-select-v2__tab ${(tab ?? 'sample') === key ? 'is-active' : ''}`}
              onClick={() => setTab(key)}
            >
              {key === 'sample' ? '샘플 시험' : key === 'full' ? '본 시험' : '틀린 문제 복습'}
              <span className="tabular">{count}</span>
            </button>
          );
        })}
      </div>
      <p className="exam-select-v2__tab-desc">
        {(tab ?? 'sample') === 'review'
          ? '시험별로 아직 못 맞힌 문제를 모았습니다. 다시 풀기는 무료 연습 모드, 오답 모의고사는 약한 영역 문제까지 채워 실전 모드로 봅니다.'
          : (tab ?? 'sample') === 'sample'
          ? '누구나 무료로 응시할 수 있는 샘플 문제입니다.'
          : user?.ai_power?.aiPower
            ? '실제 시험과 같은 문항 수·영역 비율의 본 시험입니다. 구독에 포함되어 모두 응시할 수 있습니다.'
            : '실제 시험과 같은 문항 수·영역 비율의 본 시험입니다. AI Power User 구독(첫 30일 무료) 또는 쿠폰 등록 후 응시할 수 있습니다.'}
      </p>

      {tab === 'review' ? (
        <Card padding="none" className="exam-select-v2__review-table">
          <ul>
            {[...reviews].sort((a, b) => b.incorrectTotal - a.incorrectTotal).map((review) => {
              const type = examTypes.find((item) => item.exam_type_id === review.examType);
              const code = (type ? examCodeOf(type) : '') || codeFromId(review.examType);
              const isSample = type ? tabOf(type) === 'sample' : /sample|test/i.test(review.examType);
              const subtitle = type?.name && type.name !== code ? type.name : isSample ? '샘플 시험' : '본 시험';
              return (
                <li key={review.examType} className="exam-select-v2__review-row">
                  <div className="exam-select-v2__review-exam">
                    {code || type?.icon ? <ExamBadge code={code} icon={type?.icon} size="sm" /> : null}
                    <div>
                      <strong>
                        {code || type?.name || review.examType}
                        {isSample && <span className="exam-select-v2__review-tag">샘플</span>}
                      </strong>
                      <span>{subtitle}</span>
                    </div>
                  </div>
                  <p className="exam-select-v2__review-count tabular">
                    틀린 문제 <strong>{review.incorrectTotal}</strong>개
                  </p>
                  <div className="exam-select-v2__review-actions">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleStartRetry(review.examType)}
                      loading={startingExam === `retry:${review.examType}`}
                      disabled={startingExam !== null}
                    >
                      다시 풀기
                    </Button>
                    <Button
                      variant={aiPower ? 'primary' : 'tertiary'}
                      size="sm"
                      onClick={() => {
                        // 유료(AI Power User) 기능: 권한이 없으면 체험·구독 안내
                        if (requireAiPower('오답 모의고사')) setConfirmReviewExamTypeId(review.examType);
                      }}
                      loading={startingExam === `review:${review.examType}`}
                      disabled={startingExam !== null}
                    >
                      {!aiPower && <FiLock aria-hidden="true" className="exam-select-v2__lock" />}오답 모의고사 {review.total}문항
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      ) : examTypes.filter((type) => tabOf(type) === (tab ?? 'sample')).length === 0 ? (
        <Card padding="none" tone="major">
          <EmptyState
            title={(tab ?? 'sample') === 'sample' ? '응시할 수 있는 샘플 시험이 없습니다' : '등록된 본 시험이 없습니다'}
            description="쿠폰을 등록했다면 관리자에게 시험 활성화를 요청하세요."
          />
        </Card>
      ) : (
        <section className="exam-select-v2__grid">
          {examTypes.filter((type) => tabOf(type) === (tab ?? 'sample')).map((examType) => (
            <Card key={examType.exam_type_id} padding="lg" shadow="md" hover className="exam-select-v2__exam-card">
              <div className="exam-select-v2__exam-head">
                <div className="exam-select-v2__exam-title-wrap">
                  {examCodeOf(examType) ? (
                    <span
                      className="exam-select-v2__exam-icon-wrap exam-select-v2__exam-icon-wrap--badge"
                      tabIndex={0}
                      aria-label={`${examType.name} 설명 보기`}
                      data-tooltip={examType.description}
                    >
                      <ExamBadge code={examCodeOf(examType)} icon={examType.icon} size="md" />
                    </span>
                  ) : examType.icon ? (
                    <span
                      className="exam-select-v2__exam-icon-wrap"
                      tabIndex={0}
                      aria-label={`${examType.name} 설명 보기`}
                      data-tooltip={examType.description}
                    >
                      <img src={examType.icon} alt={examType.name} className="exam-select-v2__exam-icon" />
                    </span>
                  ) : (
                    <span
                      className="exam-select-v2__exam-icon-wrap exam-select-v2__exam-icon-wrap--placeholder"
                      tabIndex={0}
                      aria-label={`${examType.name} 설명 보기`}
                      data-tooltip={examType.description}
                    >
                      <span className="exam-select-v2__exam-icon exam-select-v2__exam-icon--placeholder">
                        <MetaIcon type="defaultExam" className="exam-select-v2__exam-icon-svg" />
                      </span>
                    </span>
                  )}
                  <div>
                    <h3>{examType.name}</h3>
                  </div>
                </div>
                {!examType.is_active ? (
                  <Badge tone="neutral">비활성</Badge>
                ) : examType.access === 'locked' ? (
                  <Badge tone="warning"><FiLock aria-hidden="true" /> 구독 필요</Badge>
                ) : (
                  <Badge tone={ACCESS_BADGE[examType.access_via || 'free']?.tone || 'success'}>{ACCESS_BADGE[examType.access_via || 'free']?.label || '응시 가능'}</Badge>
                )}
              </div>

              <div className="exam-select-v2__meta-grid">
                <div className="exam-select-v2__meta-item">
                  <span className="exam-select-v2__meta-icon" aria-hidden="true">
                    <MetaIcon type="questions" className="exam-select-v2__meta-icon-svg" />
                  </span>
                  <strong>{examType.total_questions}문항</strong>
                </div>
                <div className="exam-select-v2__meta-item">
                  <span className="exam-select-v2__meta-icon" aria-hidden="true">
                    <MetaIcon type="duration" className="exam-select-v2__meta-icon-svg" />
                  </span>
                  <strong>{examType.exam_duration_minutes}분</strong>
                </div>
                <div className="exam-select-v2__meta-item">
                  <span className="exam-select-v2__meta-icon" aria-hidden="true">
                    <MetaIcon type="passing" className="exam-select-v2__meta-icon-svg" />
                  </span>
                  <strong>합격 {examType.passing_score}%</strong>
                </div>
                <div className="exam-select-v2__meta-item">
                  <span className="exam-select-v2__meta-icon" aria-hidden="true">
                    <MetaIcon type="format" className="exam-select-v2__meta-icon-svg" />
                  </span>
                  <strong>{getExamFormatLabel(examType.exam_format)}</strong>
                </div>
              </div>

              <div className="exam-select-v2__exam-actions">
                <Button
                  variant={examType.access === 'locked' ? 'secondary' : 'primary'}
                  size="lg"
                  fullWidth
                  loading={startingExam === examType.exam_type_id}
                  disabled={!examType.is_active || startingExam !== null}
                  onClick={() => handleOpenStartConfirm(examType.exam_type_id)}
                >
                  {startingExam === examType.exam_type_id
                    ? '세션 준비 중...'
                    : examType.access === 'locked'
                      ? '구독하고 응시하기'
                      : selectedMode === 'practice' ? '연습 시작' : '시험 시작'}
                </Button>
              </div>
            </Card>
          ))}
        </section>
      )}

      <AdBanner
        showAds={showAds}
        adSlot="7731352951"
        format="auto"
        className="exam-select-v2__ad"
      />

      {shortExam && (
        <div className="exam-select-v2__confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="exam-short-title">
          <div className="exam-select-v2__confirm-modal">
            <h3 id="exam-short-title">문항 수가 실제 시험보다 적어요</h3>
            <p className="exam-select-v2__confirm-text">{shortExamMessage(shortExam)}</p>
            <div className="exam-select-v2__confirm-actions">
              <Button variant="secondary" onClick={() => setShortExam(null)} disabled={startingExam !== null}>
                취소
              </Button>
              <Button
                variant="primary"
                loading={startingExam !== null}
                onClick={async () => {
                  const { examTypeId, kind } = shortExam;
                  setShortExam(null);
                  if (kind === 'incorrect-review') await handleStartIncorrectReview(examTypeId, true);
                  else await handleStartExam(examTypeId, true);
                }}
              >
                {shortExam.available}문항으로 응시
              </Button>
            </div>
          </div>
        </div>
      )}

      {confirmReviewExamTypeId && (
        <div className="exam-select-v2__confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="review-start-confirm-title">
          <div className="exam-select-v2__confirm-modal">
            <h3 id="review-start-confirm-title">오답 모의고사 안내</h3>
            <ul>
              <li>실전 모드로 진행되며, 끝날 때까지 정답과 해설을 보여 주지 않습니다.</li>
              <li>틀렸던 문제와 보충 문제가 섞여 나오고, 보기 순서도 다시 섞입니다.</li>
              <li>여기서 또 틀린 문제는 다음 오답 모의고사에 다시 들어갑니다.</li>
              <li>시간이 종료되면 자동 제출됩니다.</li>
            </ul>
            <div className="exam-select-v2__confirm-actions">
              <Button variant="secondary" onClick={() => setConfirmReviewExamTypeId(null)}>
                취소
              </Button>
              <Button
                variant="primary"
                onClick={async () => {
                  const examTypeId = confirmReviewExamTypeId;
                  setConfirmReviewExamTypeId(null);
                  await handleStartIncorrectReview(examTypeId);
                }}
              >
                시작
              </Button>
            </div>
          </div>
        </div>
      )}

      {confirmExamTypeId && (
        <div className="exam-select-v2__confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="exam-start-confirm-title">
          <div className="exam-select-v2__confirm-modal">
            <h3 id="exam-start-confirm-title">응시 전 안내</h3>
            <ul>
              <li>시험 시작 후에는 세션이 자동 저장됩니다.</li>
              <li>시간이 종료되면 자동 제출됩니다.</li>
              <li>문항에 따라 복수 정답이 존재할 수 있습니다.</li>
              <li>시험 완료 후 상세 결과와 해설을 확인할 수 있습니다.</li>
            </ul>
            <div className="exam-select-v2__confirm-actions">
              <Button variant="secondary" onClick={() => setConfirmExamTypeId(null)} disabled={startingExam !== null}>
                취소
              </Button>
              <Button variant="primary" onClick={handleConfirmStartExam} loading={startingExam !== null}>
                확인
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamSelectionPage;
